import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { trackWhatsAppClick, trackEventCustom } from "../../utils/analytics";

export default function HealthcareSoftware() {
  useEffect(() => {
    document.title =
      "Healthcare Software Development Company in Pune | HexxaTech";

    const description =
      "HexxaTech develops custom healthcare software for clinics, hospitals and healthcare businesses, including patient management, appointments, medical records, billing, treatment workflows and reporting.";

    const setMeta = (name, content) => {
      let meta = document.querySelector(`meta[name="${name}"]`);

      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", name);
        document.head.appendChild(meta);
      }

      meta.setAttribute("content", content);
    };

    setMeta("description", description);

    let canonical = document.querySelector('link[rel="canonical"]');

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute(
      "href",
      "https://hexxatech.co.in/services/healthcare-software"
    );

    const setPropertyMeta = (property, content) => {
      let meta = document.querySelector(`meta[property="${property}"]`);

      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("property", property);
        document.head.appendChild(meta);
      }

      meta.setAttribute("content", content);
    };

    setPropertyMeta(
      "og:title",
      "Healthcare Software Development Company in Pune | HexxaTech"
    );

    setPropertyMeta("og:description", description);
    setPropertyMeta("og:type", "website");
    setPropertyMeta(
      "og:url",
      "https://hexxatech.co.in/services/healthcare-software"
    );
    setPropertyMeta("og:site_name", "HexxaTech Softwares");

    const existingSchema = document.getElementById(
      "hexxatech-healthcare-schema"
    );

    if (!existingSchema) {
      const script = document.createElement("script");

      script.id = "hexxatech-healthcare-schema";
      script.type = "application/ld+json";

      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Healthcare Software Development",
        serviceType: "Healthcare Software Development",
        provider: {
          "@type": "Organization",
          name: "HexxaTech Softwares",
          url: "https://hexxatech.co.in/",
        },
        areaServed: [
          {
            "@type": "City",
            name: "Pune",
          },
          {
            "@type": "Country",
            name: "India",
          },
        ],
        description: description,
        url: "https://hexxatech.co.in/services/healthcare-software",
      });

      document.head.appendChild(script);
    }
  }, []);

  const features = [
    {
      number: "01",
      title: "Patient Management",
      text: "Maintain centralized patient information including personal details, medical history, treatment information and visit records.",
    },
    {
      number: "02",
      title: "Appointment Management",
      text: "Manage appointments, schedules, follow-ups and patient visits through a structured digital workflow.",
    },
    {
      number: "03",
      title: "Medical Records",
      text: "Store and manage patient medical information, treatment history, clinical notes and relevant healthcare records.",
    },
    {
      number: "04",
      title: "Treatment Management",
      text: "Organize treatment sessions, procedures, assessments and follow-up activities according to your healthcare workflow.",
    },
    {
      number: "05",
      title: "Billing & Payments",
      text: "Manage service billing, payment records and transaction information as part of a centralized healthcare system.",
    },
    {
      number: "06",
      title: "Doctor & Staff Management",
      text: "Manage doctors, therapists, healthcare professionals and operational staff with role-based workflows.",
    },
    {
      number: "07",
      title: "Reports & Dashboards",
      text: "Generate useful operational reports and dashboards for appointments, patients, treatments, billing and business performance.",
    },
    {
      number: "08",
      title: "Workflow Automation",
      text: "Automate repetitive healthcare processes and reduce manual data entry across different departments and users.",
    },
  ];

  const benefits = [
    "Centralized patient information",
    "Reduced manual paperwork",
    "Better appointment visibility",
    "Structured treatment workflows",
    "Improved operational efficiency",
    "Faster access to patient information",
    "Centralized billing and reporting",
    "Scalable healthcare technology",
  ];

  const healthcareSolutions = [
    "Clinic Management Software",
    "Hospital Management Software",
    "Physiotherapy Management Software",
    "Patient Management Systems",
    "Appointment Management Software",
    "Medical Record Management",
    "Healthcare Billing Software",
    "Treatment & Therapy Management",
    "Healthcare Dashboards",
    "Custom Healthcare Applications",
  ];

  const process = [
    {
      number: "01",
      title: "Understand Your Healthcare Workflow",
      text: "We first understand how your clinic, hospital or healthcare organization currently manages patients, appointments, treatments, billing and reporting.",
    },
    {
      number: "02",
      title: "Design the Solution",
      text: "We define modules, user roles, database structure, workflows and reporting requirements around your operational needs.",
    },
    {
      number: "03",
      title: "Develop the Application",
      text: "Our development team builds the healthcare software module-by-module with practical interfaces and business-focused functionality.",
    },
    {
      number: "04",
      title: "Test & Validate",
      text: "The system is tested against real workflows to identify functional issues and ensure the application behaves as expected.",
    },
    {
      number: "05",
      title: "Deploy & Support",
      text: "After deployment, we provide technical support, maintenance and future enhancements as your healthcare operations evolve.",
    },
  ];

  const serviceNavigation = [
    { title: "Custom Software Development", to: "/services/custom-software-development" },
    { title: "ERP & Business Management", to: "/services/erp-business-management" },
    { title: "CRM & Lead Management", to: "/services/crm-lead-management" },
    { title: "Healthcare Software", to: "/services/healthcare-software" },
    { title: "Inventory Management", to: "/services/inventory-management" },
    { title: "Industrial Process Software", to: "/services/industrial-process-software" },
    { title: "License Management", to: "/services/license-management" },
    { title: "Mobile App Development", to: "/services/mobile-app-development" },
  ];

  const currentIndex = serviceNavigation.findIndex(
    (service) => service.to === "/services/healthcare-software"
  );
  const previousService =
    serviceNavigation[(currentIndex - 1 + serviceNavigation.length) % serviceNavigation.length];
  const nextService =
    serviceNavigation[(currentIndex + 1) % serviceNavigation.length];

  return (
    <div className="hexxa-healthcare-page">
      <style>{`
        * {
          box-sizing: border-box;
        }

        html {
          scroll-behavior: smooth;
        }

        .hexxa-healthcare-page {
          background: #ffffff;
          color: #0f172a;
          overflow: hidden;
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI",
            Roboto, Helvetica, Arial, sans-serif;
        }

        .health-container {
          width: min(1180px, calc(100% - 40px));
          margin: 0 auto;
        }

        /* =========================
           HERO
        ========================= */

        .health-hero {
          position: relative;
          padding: 105px 0 95px;
          color: #ffffff;
          background:
            radial-gradient(
              circle at 85% 15%,
              rgba(96, 165, 250, 0.24),
              transparent 32%
            ),
            linear-gradient(
              135deg,
              #294a7d 0%,
              #355b91 55%,
              #294a7d 100%
            );
        }

        .health-hero::before {
          content: "";
          position: absolute;
          width: 420px;
          height: 420px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.035);
          top: -220px;
          right: -100px;
        }

        .health-hero-grid {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: 1.05fr 0.95fr;
          gap: 70px;
          align-items: center;
        }

        .health-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 7px 12px;
          margin-bottom: 20px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.16);
          color: #dbeafe;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1.2px;
          text-transform: uppercase;
        }

        .health-eyebrow-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #60a5fa;
        }

        .health-hero h1 {
          margin: 0;
          max-width: 720px;
          font-size: clamp(42px, 5vw, 65px);
          line-height: 1.03;
          letter-spacing: -2.8px;
          font-weight: 800;
        }

        .health-hero h1 span {
          color: #60a5fa;
        }

        .health-hero-text {
          max-width: 650px;
          margin: 25px 0 30px;
          color: #dbeafe;
          font-size: 17px;
          line-height: 1.8;
        }

        .health-buttons {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .health-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 48px;
          padding: 0 20px;
          border-radius: 10px;
          text-decoration: none;
          font-size: 14px;
          font-weight: 700;
          transition: 0.25s ease;
        }

        .health-btn-primary {
          background: #2563eb;
          color: #ffffff;
          box-shadow: 0 12px 25px rgba(15, 23, 42, 0.2);
        }

        .health-btn-primary:hover {
          transform: translateY(-2px);
          background: #1d4ed8;
        }

        .health-btn-light {
          background: rgba(255, 255, 255, 0.08);
          color: #ffffff;
          border: 1px solid rgba(255, 255, 255, 0.22);
        }

        .health-btn-light:hover {
          background: rgba(255, 255, 255, 0.15);
        }

        /* =========================
           HERO VISUAL
        ========================= */

        .health-visual {
          position: relative;
          min-height: 370px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .health-dashboard {
          width: min(470px, 100%);
          padding: 17px;
          border-radius: 22px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.18);
          backdrop-filter: blur(10px);
          box-shadow: 0 30px 70px rgba(15, 23, 42, 0.2);
          transform: rotate(-2deg);
        }

        .health-dashboard-top {
          display: flex;
          gap: 6px;
          padding-bottom: 14px;
        }

        .health-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.6);
        }

        .health-dashboard-content {
          display: grid;
          grid-template-columns: 80px 1fr;
          gap: 12px;
        }

        .health-sidebar {
          min-height: 260px;
          padding: 12px 8px;
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.1);
        }

        .health-side-line {
          height: 8px;
          margin-bottom: 12px;
          border-radius: 5px;
          background: rgba(255, 255, 255, 0.2);
        }

        .health-side-line.active {
          background: #60a5fa;
        }

        .health-main {
          display: grid;
          gap: 12px;
        }

        .health-summary {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
        }

        .health-summary-card {
          padding: 14px;
          min-height: 78px;
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.11);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .health-summary-card strong {
          display: block;
          margin-bottom: 5px;
          font-size: 18px;
        }

        .health-summary-card small {
          color: #cbd5e1;
          font-size: 9px;
        }

        .health-patient-list {
          padding: 15px;
          min-height: 165px;
          border-radius: 14px;
          background: rgba(255, 255, 255, 0.09);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .health-patient-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding: 11px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        }

        .health-patient-row:last-child {
          border-bottom: 0;
        }

        .health-patient-name {
          display: flex;
          align-items: center;
          gap: 9px;
        }

        .health-avatar {
          width: 25px;
          height: 25px;
          border-radius: 50%;
          background: rgba(96, 165, 250, 0.35);
        }

        .health-patient-name span {
          width: 70px;
          height: 7px;
          border-radius: 4px;
          background: rgba(255, 255, 255, 0.25);
        }

        .health-status {
          width: 42px;
          height: 7px;
          border-radius: 4px;
          background: #60a5fa;
        }

        /* =========================
           COMMON
        ========================= */

        .health-section {
          padding: 90px 0;
        }

        .health-section-soft {
          background: #f8fafc;
        }

        .health-label {
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }

        .health-title {
          margin: 12px 0 0;
          max-width: 780px;
          font-size: clamp(32px, 4vw, 48px);
          line-height: 1.1;
          letter-spacing: -1.8px;
          color: #0f172a;
        }

        .health-description {
          margin: 18px 0 0;
          max-width: 720px;
          color: #64748b;
          font-size: 16px;
          line-height: 1.8;
        }

        /* =========================
           INTRO
        ========================= */

        .health-intro-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 70px;
          align-items: center;
        }

        .health-intro-text {
          margin-top: 22px;
          color: #64748b;
          font-size: 15px;
          line-height: 1.85;
        }

        .health-highlight-box {
          padding: 34px;
          border-radius: 22px;
          background: linear-gradient(145deg, #eff6ff, #ffffff);
          border: 1px solid #dbeafe;
        }

        .health-highlight-box h3 {
          margin: 0 0 20px;
          font-size: 22px;
        }

        .health-highlight-list {
          display: grid;
          gap: 11px;
        }

        .health-highlight-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          border-radius: 10px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          color: #475569;
          font-size: 13px;
        }

        .health-highlight-check {
          width: 23px;
          height: 23px;
          flex-shrink: 0;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #eff6ff;
          color: #2563eb;
          font-size: 12px;
          font-weight: 800;
        }

        /* =========================
           FEATURES
        ========================= */

        .health-features-header {
          max-width: 800px;
          margin: 0 auto 45px;
          text-align: center;
        }

        .health-features-header .health-title,
        .health-features-header .health-description {
          margin-left: auto;
          margin-right: auto;
        }

        .health-feature-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }

        .health-feature-card {
          min-height: 250px;
          padding: 25px;
          border-radius: 16px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          transition: 0.3s ease;
        }

        .health-feature-card:hover {
          transform: translateY(-5px);
          border-color: #bfdbfe;
          box-shadow: 0 18px 40px rgba(15, 23, 42, 0.07);
        }

        .health-feature-number {
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1px;
        }

        .health-feature-card h3 {
          margin: 16px 0 9px;
          font-size: 17px;
        }

        .health-feature-card p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.75;
        }

        /* =========================
           SOLUTIONS
        ========================= */

        .health-solutions-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 70px;
          align-items: start;
        }

        .health-solutions-list {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-top: 30px;
        }

        .health-solution-item {
          padding: 15px;
          border: 1px solid #e5e7eb;
          border-radius: 11px;
          background: #ffffff;
          color: #475569;
          font-size: 13px;
          font-weight: 600;
        }

        .health-solution-item::before {
          content: "✓";
          margin-right: 8px;
          color: #2563eb;
          font-weight: 800;
        }

        .health-benefits-box {
          padding: 32px;
          border-radius: 22px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          box-shadow: 0 20px 50px rgba(15, 23, 42, 0.06);
        }

        .health-benefits-box h3 {
          margin: 0 0 22px;
          font-size: 21px;
        }

        .health-benefits {
          display: grid;
          gap: 11px;
        }

        .health-benefit {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #475569;
          font-size: 13px;
        }

        .health-benefit span {
          width: 22px;
          height: 22px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: #eff6ff;
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
        }

        /* =========================
           PROCESS
        ========================= */

        .health-process-grid {
          display: grid;
          grid-template-columns: 0.9fr 1.1fr;
          gap: 70px;
          align-items: start;
        }

        .health-process-list {
          display: grid;
          gap: 10px;
          margin-top: 35px;
        }

        .health-process-item {
          padding: 20px;
          border-radius: 14px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          transition: 0.25s ease;
        }

        .health-process-item:hover {
          border-color: #bfdbfe;
          transform: translateX(4px);
        }

        .health-process-number {
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1px;
        }

        .health-process-item h3 {
          margin: 7px 0;
          font-size: 15px;
        }

        .health-process-item p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.7;
        }

        .health-process-visual {
          padding: 35px;
          border-radius: 24px;
          background: linear-gradient(145deg, #eff6ff, #ffffff);
          border: 1px solid #dbeafe;
        }

        .health-flow {
          display: grid;
          gap: 10px;
          margin-top: 25px;
        }

        .health-flow-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          border-radius: 12px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
        }

        .health-flow-number {
          width: 30px;
          height: 30px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #eff6ff;
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
        }

        .health-flow-row strong {
          font-size: 12px;
        }

        /* =========================
           CTA
        ========================= */

        .health-cta {
          padding: 85px 0;
        }

        .health-cta-box {
          position: relative;
          overflow: hidden;
          padding: 70px 30px;
          text-align: center;
          border-radius: 24px;
          color: #ffffff;
          background:
            radial-gradient(
              circle at 85% 15%,
              rgba(59, 130, 246, 0.3),
              transparent 30%
            ),
            linear-gradient(135deg, #0f172a, #172554);
        }

        .health-cta-box::before {
          content: "";
          position: absolute;
          width: 300px;
          height: 300px;
          border-radius: 50%;
          border: 1px solid rgba(255, 255, 255, 0.08);
          top: -170px;
          left: -80px;
        }

        .health-cta-box > * {
          position: relative;
          z-index: 1;
        }

        .health-cta-label {
          display: inline-block;
          padding: 7px 12px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #bfdbfe;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .health-cta h2 {
          max-width: 750px;
          margin: 20px auto 12px;
          font-size: clamp(34px, 4vw, 50px);
          line-height: 1.1;
          letter-spacing: -1.5px;
        }

        .health-cta p {
          max-width: 680px;
          margin: 0 auto 28px;
          color: #cbd5e1;
          font-size: 15px;
          line-height: 1.8;
        }

        .health-cta .health-btn-primary {
          background: #ffffff;
          color: #0f172a;
        }

        .health-cta .health-btn-primary:hover {
          background: #eff6ff;
        }

        /* =========================
           SEO CONTENT
        ========================= */

        .health-seo-content {
          padding: 0 0 75px;
        }

        .health-seo-content-inner {
          max-width: 900px;
        }

        .health-seo-content h2 {
          margin: 0 0 15px;
          color: #0f172a;
          font-size: 25px;
        }

        .health-seo-content h3 {
          margin: 28px 0 10px;
          color: #0f172a;
          font-size: 19px;
        }

        .health-seo-content p {
          margin: 0 0 15px;
          color: #64748b;
          font-size: 14px;
          line-height: 1.85;
        }

        /* =========================
           RESPONSIVE
        ========================= */

        @media (max-width: 1000px) {
          .health-hero-grid {
            grid-template-columns: 1fr;
            gap: 45px;
          }

          .health-feature-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .health-intro-grid,
          .health-solutions-grid,
          .health-process-grid {
            grid-template-columns: 1fr;
            gap: 45px;
          }
        }

        @media (max-width: 700px) {
          .health-container {
            width: min(100% - 28px, 1180px);
          }

          .health-hero {
            padding: 75px 0 65px;
          }

          .health-hero h1 {
            font-size: 42px;
            letter-spacing: -1.8px;
          }

          .health-hero-text {
            font-size: 15px;
          }

          .health-dashboard {
            transform: none;
          }

          .health-section {
            padding: 65px 0;
          }

          .health-feature-grid {
            grid-template-columns: 1fr;
          }

          .health-solutions-list {
            grid-template-columns: 1fr;
          }

          .health-process-visual,
          .health-benefits-box,
          .health-highlight-box {
            padding: 22px;
          }

          .health-dashboard-content {
            grid-template-columns: 55px 1fr;
          }

          .health-sidebar {
            min-height: 210px;
          }

          .health-summary {
            grid-template-columns: 1fr;
          }

          .health-cta {
            padding: 65px 0;
          }

          .health-cta-box {
            padding: 50px 20px;
            border-radius: 18px;
          }
        }
      `}</style>

      {/* =========================================
          HERO
      ========================================= */}

      <section className="health-hero">
        <div className="health-container health-hero-grid">
          <div>
            <div className="health-eyebrow">
              <span className="health-eyebrow-dot"></span>
              Healthcare Software Development
            </div>

            <h1>
              Healthcare software built around
              <br />
              <span>your clinical workflow.</span>
            </h1>

            <p className="health-hero-text">
              HexxaTech develops custom healthcare software for clinics,
              hospitals, physiotherapy centers and healthcare businesses.
              Manage patients, appointments, treatment workflows, medical
              records, billing and reporting through software designed around
              your organization.
            </p>

            <div className="health-buttons">
              <a
                href="https://wa.me/919226591664?text=Hello%20HexxaTech%2C%20I%20would%20like%20to%20discuss%20a%20healthcare%20software%20project."
                target="_blank"
                rel="noopener noreferrer"
                className="health-btn health-btn-primary"
                onClick={() =>
                  trackWhatsAppClick("healthcare_service_hero")
                }
              >
                Discuss Your Healthcare Project →
              </a>

              <Link
                to="/case-studies/swasthya"
                className="health-btn health-btn-light"
                onClick={() =>
                  trackEventCustom("healthcare_case_study_click", {
                    location: "healthcare_hero",
                  })
                }
              >
                View Healthcare Case Study →
              </Link>
            </div>
          </div>

          <div className="health-visual">
            <div className="health-dashboard">
              <div className="health-dashboard-top">
                <span className="health-dot"></span>
                <span className="health-dot"></span>
                <span className="health-dot"></span>
              </div>

              <div className="health-dashboard-content">
                <div className="health-sidebar">
                  <div className="health-side-line active"></div>
                  <div className="health-side-line"></div>
                  <div className="health-side-line"></div>
                  <div className="health-side-line"></div>
                  <div className="health-side-line"></div>
                  <div className="health-side-line"></div>
                </div>

                <div className="health-main">
                  <div className="health-summary">
                    <div className="health-summary-card">
                      <strong>Patients</strong>
                      <small>Patient Management</small>
                    </div>

                    <div className="health-summary-card">
                      <strong>Visits</strong>
                      <small>Appointments</small>
                    </div>

                    <div className="health-summary-card">
                      <strong>Care</strong>
                      <small>Treatment Workflow</small>
                    </div>
                  </div>

                  <div className="health-patient-list">
                    <div className="health-patient-row">
                      <div className="health-patient-name">
                        <div className="health-avatar"></div>
                        <span></span>
                      </div>

                      <div className="health-status"></div>
                    </div>

                    <div className="health-patient-row">
                      <div className="health-patient-name">
                        <div className="health-avatar"></div>
                        <span></span>
                      </div>

                      <div className="health-status"></div>
                    </div>

                    <div className="health-patient-row">
                      <div className="health-patient-name">
                        <div className="health-avatar"></div>
                        <span></span>
                      </div>

                      <div className="health-status"></div>
                    </div>

                    <div className="health-patient-row">
                      <div className="health-patient-name">
                        <div className="health-avatar"></div>
                        <span></span>
                      </div>

                      <div className="health-status"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          INTRO
      ========================================= */}

      <section className="health-section">
        <div className="health-container health-intro-grid">
          <div>
            <span className="health-label">
              Healthcare Technology
            </span>

            <h2 className="health-title">
              Software that connects patients, people and processes.
            </h2>

            <p className="health-intro-text">
              Healthcare organizations often manage large amounts of patient
              information, appointments, treatment records, billing data and
              operational activities every day.
            </p>

            <p className="health-intro-text">
              HexxaTech builds custom healthcare applications that bring these
              processes together into a centralized digital system. Instead of
              forcing your organization to change its workflow around generic
              software, we design the application around the way your team
              actually works.
            </p>

            <p className="health-intro-text">
              Our healthcare solutions can be designed for clinics, hospitals,
              physiotherapy centers, medical organizations and other
              healthcare businesses.
            </p>
          </div>

          <div className="health-highlight-box">
            <h3>
              What your healthcare software can manage
            </h3>

            <div className="health-highlight-list">
              {[
                "Patients & Medical Information",
                "Appointments & Scheduling",
                "Treatment & Follow-ups",
                "Billing & Payments",
                "Doctors & Staff",
                "Reports & Dashboards",
                "Operational Workflows",
                "Centralized Data",
              ].map((item) => (
                <div className="health-highlight-item" key={item}>
                  <span className="health-highlight-check">✓</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          FEATURES
      ========================================= */}

      <section className="health-section health-section-soft">
        <div className="health-container">
          <div className="health-features-header">
            <span className="health-label">
              Healthcare Software Features
            </span>

            <h2 className="health-title">
              One platform for your healthcare operations.
            </h2>

            <p className="health-description">
              We develop healthcare management systems with modules and
              workflows based on your organization's specific requirements.
            </p>
          </div>

          <div className="health-feature-grid">
            {features.map((feature) => (
              <article
                className="health-feature-card"
                key={feature.number}
                onClick={() =>
                  trackEventCustom("healthcare_feature_interest", {
                    feature: feature.title,
                  })
                }
              >
                <div className="health-feature-number">
                  {feature.number}
                </div>

                <h3>{feature.title}</h3>

                <p>{feature.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          SOLUTIONS
      ========================================= */}

      <section className="health-section">
        <div className="health-container health-solutions-grid">
          <div>
            <span className="health-label">
              Healthcare Solutions
            </span>

            <h2 className="health-title">
              Built for different healthcare environments.
            </h2>

            <p className="health-description">
              Every healthcare organization has different processes. Our
              custom development approach allows us to build software around
              your users, services, workflows and operational requirements.
            </p>

            <div className="health-solutions-list">
              {healthcareSolutions.map((solution) => (
                <div
                  className="health-solution-item"
                  key={solution}
                >
                  {solution}
                </div>
              ))}
            </div>
          </div>

          <div className="health-benefits-box">
            <h3>
              Benefits of custom healthcare software
            </h3>

            <div className="health-benefits">
              {benefits.map((benefit) => (
                <div className="health-benefit" key={benefit}>
                  <span>✓</span>
                  {benefit}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          DEVELOPMENT PROCESS
      ========================================= */}

      <section className="health-section health-section-soft">
        <div className="health-container health-process-grid">
          <div>
            <span className="health-label">
              Our Development Process
            </span>

            <h2 className="health-title">
              From healthcare requirements to working software.
            </h2>

            <p className="health-description">
              We follow a structured development process to ensure the
              software reflects your actual healthcare operations.
            </p>

            <div className="health-process-list">
              {process.map((step) => (
                <div
                  className="health-process-item"
                  key={step.number}
                >
                  <div className="health-process-number">
                    {step.number}
                  </div>

                  <h3>{step.title}</h3>

                  <p>{step.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="health-process-visual">
            <span className="health-label">
              Healthcare Workflow
            </span>

            <h3
              style={{
                margin: "12px 0 0",
                fontSize: "23px",
              }}
            >
              A system designed around your workflow.
            </h3>

            <div className="health-flow">
              {[
                "Patient Registration",
                "Appointment & Scheduling",
                "Consultation / Assessment",
                "Treatment & Medical Records",
                "Billing & Payments",
                "Reports & Follow-up",
              ].map((item, index) => (
                <div className="health-flow-row" key={item}>
                  <div className="health-flow-number">
                    {String(index + 1).padStart(2, "0")}
                  </div>

                  <strong>{item}</strong>
                </div>
              ))}
            </div>

            <p
              style={{
                margin: "22px 0 0",
                color: "#64748b",
                fontSize: "13px",
                lineHeight: 1.7,
              }}
            >
              Modules can be connected according to the actual workflow of
              your healthcare organization, creating a centralized system
              instead of disconnected tools and spreadsheets.
            </p>
          </div>
        </div>
      </section>

      {/* =========================================
          CASE STUDY
      ========================================= */}

      <section className="health-section">
        <div className="health-container">
          <div
            style={{
              textAlign: "center",
              maxWidth: "780px",
              margin: "0 auto",
            }}
          >
            <span className="health-label">
              Healthcare Case Study
            </span>

            <h2 className="health-title">
              See how custom healthcare software can work in practice.
            </h2>

            <p
              className="health-description"
              style={{
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
              Explore Swasthya, a healthcare management solution developed to
              support patient records, appointments, treatment information and
              day-to-day healthcare operations.
            </p>

            <div style={{ marginTop: "28px" }}>
              <Link
                to="/case-studies/swasthya"
                className="health-btn health-btn-primary"
                onClick={() =>
                  trackEventCustom("healthcare_case_study_click", {
                    location: "healthcare_case_study_section",
                  })
                }
              >
                Explore Swasthya Case Study →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          SERVICE NAVIGATION
      ========================================= */}

      <section
        style={{
          padding: "0 0 70px",
          background: "#ffffff",
        }}
      >
        <div
          style={{
            width: "min(100% - 32px, 1100px)",
            margin: "0 auto",
            maxWidth: "1100px",
          }}
        >
          <span className="health-label">
            Explore Our Services
          </span>

          <h2 className="health-title" style={{ marginTop: "12px" }}>
            Explore our other software development services.
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "12px",
              marginTop: "22px",
            }}
          >
            {serviceNavigation.map((service) => {
              const isCurrent = service.to === "/services/healthcare-software";

              return (
                <Link
                  key={service.to}
                  to={service.to}
                  style={{
                    display: "block",
                    padding: "16px 18px",
                    borderRadius: "12px",
                    textDecoration: "none",
                    border: isCurrent ? "1px solid #2563eb" : "1px solid #e5e7eb",
                    background: isCurrent ? "#eff6ff" : "#f8fafc",
                    color: isCurrent ? "#1d4ed8" : "#334155",
                    fontWeight: isCurrent ? 700 : 600,
                  }}
                >
                  {service.title}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section
        style={{
          padding: "0 0 70px",
          background: "#ffffff",
        }}
      >
        <div
          style={{
            width: "min(100% - 32px, 1100px)",
            margin: "0 auto",
            maxWidth: "1100px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "12px",
              flexWrap: "wrap",
              borderTop: "1px solid #e5e7eb",
              paddingTop: "20px",
            }}
          >
            <Link
              to={previousService.to}
              style={{ color: "#2563eb", textDecoration: "none", fontWeight: 600 }}
            >
              ← Previous: {previousService.title}
            </Link>

            <Link
              to={nextService.to}
              style={{ color: "#2563eb", textDecoration: "none", fontWeight: 600 }}
            >
              Next: {nextService.title} →
            </Link>
          </div>
        </div>
      </section>

      {/* =========================================
          CTA
      ========================================= */}

      <section className="health-cta">
        <div className="health-container">
          <div className="health-cta-box">
            <span className="health-cta-label">
              Start Your Healthcare Project
            </span>

            <h2>
              Need healthcare software built around your organization?
            </h2>

            <p>
              Tell us about your clinic, hospital or healthcare workflow. We
              can help you plan and develop a custom healthcare management
              solution based on your operational requirements.
            </p>

            <a
              href="https://wa.me/919226591664?text=Hello%20HexxaTech%2C%20I%20would%20like%20to%20discuss%20a%20healthcare%20software%20project."
              target="_blank"
              rel="noopener noreferrer"
              className="health-btn health-btn-primary"
              onClick={() =>
                trackWhatsAppClick("healthcare_service_cta")
              }
            >
              Discuss Your Healthcare Project →
            </a>
          </div>
        </div>
      </section>

      {/* =========================================
          SEO CONTENT
      ========================================= */}

      <section className="health-seo-content">
        <div className="health-container health-seo-content-inner">
          <h2>
            Healthcare Software Development Company in Pune
          </h2>

          <p>
            HexxaTech Softwares is a healthcare software development company
            providing custom healthcare management solutions for clinics,
            hospitals, physiotherapy centers and healthcare businesses in Pune
            and across India. Our software solutions can include patient
            management, appointment scheduling, medical records, treatment
            management, billing, reporting and business automation.
          </p>

          <p>
            Our approach to healthcare software development focuses on
            understanding the actual workflow of the healthcare organization
            before designing and developing the application. This helps create
            software that is practical for doctors, therapists,
            administrators, reception teams and other users involved in daily
            operations.
          </p>

          <h3>
            Custom Healthcare Management Software
          </h3>

          <p>
            Healthcare businesses often have unique requirements that cannot
            always be handled effectively by generic software. HexxaTech can
            develop custom healthcare management systems with modules,
            workflows, dashboards and reports based on specific operational
            requirements.
          </p>

          <h3>
            Healthcare Software Development Services in Pune
          </h3>

          <p>
            Our healthcare software development services are available for
            organizations looking for customized digital solutions in Pune and
            across India. We can help businesses digitize patient management,
            appointments, treatment workflows, medical information, billing,
            reporting and other operational processes through a centralized
            software platform.
          </p>

          <h3>
            Healthcare Software Built Around Your Workflow
          </h3>

          <p>
            Instead of asking healthcare teams to adapt their processes to
            rigid software, we build applications around their existing
            workflows. The result is a more practical system that can evolve
            as the organization grows and its operational requirements change.
          </p>
        </div>
      </section>
    </div>
  );
}